// Helpers de date/temps. Clés de bucket en heure locale (la journée
// alimentaire suit le fuseau du navigateur, pas l'UTC).
import {
  differenceInCalendarDays,
  format,
  getISOWeek,
  getISOWeekYear,
  parseISO,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';
import type { Granularity } from './types';

// Tolère le séparateur espace (format PocketBase) en le normalisant en « T ».
export function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  return parseISO(value.includes(' ') ? value.replace(' ', 'T') : value);
}

// Clé jour locale « YYYY-MM-DD ».
export function dayKey(value: string | Date): string {
  return format(toDate(value), 'yyyy-MM-dd');
}

// Jour courant en heure locale (pas toISOString(), qui renvoie l'UTC).
export function todayKey(): string {
  return dayKey(new Date());
}

// Format `<input type="datetime-local">` en heure locale (pas toISOString()).
export function nowLocalInputValue(now: Date = new Date()): string {
  return format(now, "yyyy-MM-dd'T'HH:mm");
}

// Clé semaine ISO « YYYY-Www » (semaine commençant le lundi).
export function weekKey(value: string | Date): string {
  const d = toDate(value);
  const week = String(getISOWeek(d)).padStart(2, '0');
  return `${getISOWeekYear(d)}-W${week}`;
}

// Clé mois locale « YYYY-MM ».
export function monthKey(value: string | Date): string {
  return format(toDate(value), 'yyyy-MM');
}

export function bucketKey(value: string | Date, granularity: Granularity): string {
  switch (granularity) {
    case 'day':
      return dayKey(value);
    case 'week':
      return weekKey(value);
    case 'month':
      return monthKey(value);
  }
}

// Début (Date) du bucket contenant `value`, pour l'axe temporel des graphes.
export function bucketStart(value: string | Date, granularity: Granularity): Date {
  const d = toDate(value);
  switch (granularity) {
    case 'day':
      return startOfDay(d);
    case 'week':
      return startOfISOWeek(d);
    case 'month':
      return startOfMonth(d);
  }
}

// Nombre de jours calendaires entre deux dates (b - a).
export function daysBetween(a: string | Date, b: string | Date): number {
  return differenceInCalendarDays(toDate(b), toDate(a));
}

// Âge en années révolues à une date de référence (par défaut aujourd'hui).
export function ageFrom(birthdate: string | Date, at: string | Date = new Date()): number {
  const b = toDate(birthdate);
  const now = toDate(at);
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
}
